import React, { useState, useEffect, useContext } from "react";
import * as Library from "../utils/Library";
import { getUserPaymentMethods, paymentMethods } from "../utils/helperFunctions";
import LayoutContext from "./LayoutContext";
export const UserContext = React.createContext();

export const UserProvider = ({ children }) => {
    const { setSelectedPaymentType } = useContext(LayoutContext);
    /* #region User Data */
    const [isUserLoggedIn, setIsUserLoggedIn] = useState(false);
    const [BOTagAccountID, setBOTagAccountID] = useState(undefined);
    const [userData, setUserData] = useState(defaultUserData);
    const [userAddress, setUserAddress] = useState(defaultAddressData);
    const [userGPSCoordinates, setUserGPSCoordinates] = useState(defaultGPSCoordinates);

    const [checkoutCartData, setCheckoutCartData] = useState(defaultCheckoutCartData);
    const [quickBuyData, setQuickBuyData] = useState(defaultCheckoutCartData);
    const [checkoutPaymentData, setCheckoutPaymentData] = useState(defaultCheckoutPaymentData);
    const [pickupDistance, setPickupDistance] = useState(50);
    const [shippingRecipient, setShippingRecipient] = useState({ firstName: null, lastName: null });
    const [productInfo, setProductInfo] = useState(null);
    const [thankYouAmount, setThankYouAmount] = useState("0");
    const [userPaymentMethods, setUserPaymentMethods] = useState({
        cardsData: [],
        defaultPaymentMethod: ""
    });
    /* #endregion */

    /* #region EFFECTS */

    useEffect(() => {
        if (localStorage.getItem("isLoggedIn") && localStorage.getItem("pid")) {
            Library.makePostRequest("getUser", { _id: localStorage.getItem("pid") }).then(res => {
                if (res?.data?.status === 200) {
                    const { _id, firstName, lastName, email, phoneNumber, cards, defaultPaymentMethod } = res?.data?.data;

                    const allUserInfo = {
                        _id,
                        firstName,
                        lastName,
                        email,
                        phone: phoneNumber,
                        cards,
                        defaultPaymentMethod
                    };

                    userLogin(
                        allUserInfo,
                        setIsUserLoggedIn,
                        setUserData,
                        setUserAddress,
                        setUserGPSCoordinates,
                        setBOTagAccountID,
                        setCheckoutPaymentData,
                        setShippingRecipient,
                        setCheckoutCartData,
                        checkoutCartData,
                        setUserPaymentMethods,
                        setSelectedPaymentType
                    );
                } else {
                    userLogout(
                        setIsUserLoggedIn,
                        setUserData,
                        setUserAddress,
                        setUserGPSCoordinates,
                        setCheckoutCartData,
                        setCheckoutPaymentData,
                        setBOTagAccountID,
                        setShippingRecipient,
                        setUserPaymentMethods,
                        setSelectedPaymentType
                    );
                }
            });
        }
    }, []);

    //TODO: REMOVE LATER... TEMPORARY FOR TESTING CHANGES

    //TODO: REMOVE LATER... TEMPORARY FOR TESTING CHANGES

    /* #endregion */

    return (
        <UserContext.Provider
            value={{
                isUserLoggedIn,
                setIsUserLoggedIn,
                BOTagAccountID,
                setBOTagAccountID,
                userData,
                setUserData,
                userAddress,
                setUserAddress,
                userGPSCoordinates,
                setUserGPSCoordinates,
                checkoutCartData,
                setCheckoutCartData,
                checkoutPaymentData,
                setCheckoutPaymentData,
                pickupDistance,
                setPickupDistance,
                shippingRecipient,
                setShippingRecipient,
                productInfo,
                setProductInfo,
                quickBuyData,
                setQuickBuyData,
                thankYouAmount,
                setThankYouAmount,
                userPaymentMethods,
                setUserPaymentMethods
            }}>
            {children}
        </UserContext.Provider>
    );
};

export default UserContext;

/* #region Default Data */
const defaultUserData = {
    firstName: undefined,
    lastName: undefined,
    email: undefined,
    phoneCountryPrefix: "+47",
    phone: undefined
};

const defaultAddressData = localStorage.getItem("userLocation")
    ? JSON.parse(localStorage.getItem("userLocation"))?.location
    : {
          street: undefined,
          streetNumber: undefined,
          city: undefined,
          country: undefined,
          postCode: undefined
      };
const defaultGPSCoordinates = localStorage.getItem("userCoords")
    ? JSON.parse(localStorage.getItem("userCoords"))?.coords
    : { latitude: undefined, longitude: undefined };

const defaultCheckoutCartData = {
    items: [],
    total: 0,
    currency: "NOK",
    currencySymbol: "kr"
};

const defaultCheckoutPaymentData = {
    cardNumber: undefined,
    cardHolder: undefined,
    cardExpiration: undefined,
    cardCVV: undefined,
    cardName: undefined,
    customerID: undefined,
    paymentMethodID: undefined
};
/* #endregion */

/* #region Methods */

export async function userLogin(
    allUserInfo,
    setIsUserLoggedIn,
    setUserData,
    setUserAddress,
    setUserGPSCoordinates,
    setBOTagAccountID,
    setCheckoutPaymentData,
    setShippingRecipient,
    setCheckoutCartData,
    checkoutCartData,
    setUserPaymentMethods,
    setSelectedPaymentType,
    isOnboarding = false
) {
    const { _id, firstName, lastName, email, phone, cards, defaultPaymentMethod } = allUserInfo;

    setIsUserLoggedIn(true);
    setUserData({ firstName, lastName, email, phone });
    setBOTagAccountID(_id);

    const defaultCard = Array.isArray(cards) ? cards.find(card => card.isDefault) : cards;

    setCheckoutPaymentData({
        cardNumber: defaultCard?.cardNumber,
        cardHolder: defaultCard?.cardHolderName,
        cardExpiration: defaultCard?.cardExpDate,
        cardCVV: defaultCard?.cardCVV,
        cardName: defaultCard?.cardName,
        customerID: defaultCard?.customerID,
        paymentMethodID: defaultCard?.paymentMethodID
    });

    setShippingRecipient({ firstName: firstName, lastName: lastName });
    setCheckoutCartData(checkoutCartData);
    if (!isOnboarding) {
        const { paymentData } = await getUserPaymentMethods({ email });
        const payload = {
            cardsData: [],
            defaultPaymentMethod
        };
        setUserPaymentMethods(paymentData?.cardsData?.length || paymentData?.defaultPaymentMethod ? paymentData : payload);
        setSelectedPaymentType(paymentData?.defaultPaymentMethod || defaultPaymentMethod);
    }
    return null;
}

export function userLogout(
    //TODO: CHECK IF ALL DATA WAS RESET ON LOGOUT
    setIsUserLoggedIn,
    setUserData,
    setUserAddress,
    setUserGPSCoordinates,
    setCheckoutCartData,
    setCheckoutPaymentData,
    setBOTagAccountID,
    setShippingRecipient,
    setUserPaymentMethods,
    setSelectedPaymentType
) {
    setIsUserLoggedIn(false);
    setUserData(defaultUserData);
    setCheckoutCartData(defaultCheckoutCartData);
    setCheckoutPaymentData(defaultCheckoutPaymentData);
    setBOTagAccountID(undefined);
    setShippingRecipient({ firstName: null, lastName: null });
    localStorage.removeItem("isLoggedIn");
    localStorage.removeItem("pid");
    setUserPaymentMethods({
        cardsData: [],
        defaultPaymentMethod: ""
    });
    setSelectedPaymentType(paymentMethods.CARD);
}

export function addItemToCart(item, checkoutCartData, setCheckoutCartData) {
    const newItems = [...checkoutCartData.items, item];
    const newTotal = checkoutCartData.total + item.price;

    setCheckoutCartData({ items: newItems, total: newTotal });
}

/* #endregion */
